import React, { useState } from 'react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import EKYBStepNav from '../components/ekyb/EKYBStepNav';
import StepJurisdiction from '../components/ekyb/StepJurisdiction';
import StepSearch from '../components/ekyb/StepSearch';
import StepRegistryData from '../components/ekyb/StepRegistryData';
import StepUBO from '../components/ekyb/StepUBO';
import StepAML from '../components/ekyb/StepAML';
import StepMiCA from '../components/ekyb/StepMiCA';
import StepReport from '../components/ekyb/StepReport';
import { useAuth } from '../hooks';
import '../styles/ekyb.css';

const EKYBVerification = () => {
  const { currentUser, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [micaRequired, setMicaRequired] = useState(false);
  const [jurisdiction, setJurisdiction] = useState(null);
  const [entity, setEntity] = useState(null);
  const [uboData, setUboData] = useState([]);
  const [amlResult, setAmlResult] = useState(null);
  const [micaResult, setMicaResult] = useState(null);
  const [verificationId, setVerificationId] = useState(null);

  // Navigation helpers
  const markStepCompleted = (stepId) => {
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId]);
    }
  };

  const goToStep = (stepId) => {
    setCurrentStep(stepId);
  };

  const nextStep = () => {
    markStepCompleted(currentStep);
    setCurrentStep(currentStep + 1);
  };

  // Step 1: Jurisdiction Selection
  const handleJurisdictionSelect = (selectedJurisdiction) => {
    setJurisdiction(selectedJurisdiction);
    nextStep();
  };

  // Step 2: Company Search
  const handleSearchComplete = (searchResult) => {
    setEntity(searchResult);
    nextStep();
  };

  // Step 3: Registry Data Review
  const handleRegistryDataContinue = () => {
    nextStep();
  };

  // Step 4: UBO Identification
  const handleUBOContinue = (ubos) => {
    setUboData(ubos);
    nextStep();
  };

  // Step 5: AML Screening Complete
  const handleAMLComplete = (screeningResult) => {
    setAmlResult(screeningResult);

    // Ask user if MiCA check is required
    const needsMiCA = window.confirm(
      'Does this entity provide crypto-asset services?\n\nClick OK to perform MiCA register check.\nClick Cancel to skip to final report.'
    );

    setMicaRequired(needsMiCA);
    nextStep();
  };

  // Step 6: MiCA Check Complete (conditional)
  const handleMiCAComplete = (checkResult) => {
    setMicaResult(checkResult);
    nextStep();
  };

  // Step 7: Report - New Verification
  const handleNewVerification = () => {
    // Reset all state
    setCurrentStep(1);
    setCompletedSteps([]);
    setMicaRequired(false);
    setJurisdiction(null);
    setEntity(null);
    setUboData([]);
    setAmlResult(null);
    setMicaResult(null);
    setVerificationId(null);
  };

  // Render current step component
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepJurisdiction
            onSelect={handleJurisdictionSelect}
            selectedJurisdiction={jurisdiction}
          />
        );

      case 2:
        return (
          <StepSearch
            jurisdiction={jurisdiction}
            onSearchComplete={handleSearchComplete}
          />
        );

      case 3:
        return (
          <StepRegistryData
            entityData={entity}
            onContinue={handleRegistryDataContinue}
          />
        );

      case 4:
        return (
          <StepUBO
            onContinue={handleUBOContinue}
          />
        );

      case 5:
        return (
          <StepAML
            entityData={entity}
            uboData={uboData}
            onScreeningComplete={handleAMLComplete}
          />
        );

      case 6:
        if (micaRequired) {
          return (
            <StepMiCA
              entityData={entity}
              jurisdiction={jurisdiction}
              onCheckComplete={handleMiCAComplete}
            />
          );
        } else {
          // Skip to report if MiCA not required
          return (
            <StepReport
              verificationData={{
                entity,
                amlResult,
                micaResult: null,
                jurisdiction,
                verificationId: verificationId || `EKYB-${Date.now()}`
              }}
              onNewVerification={handleNewVerification}
            />
          );
        }

      case 7:
        return (
          <StepReport
            verificationData={{
              entity,
              amlResult,
              micaResult,
              jurisdiction,
              verificationId: verificationId || `EKYB-${Date.now()}`
            }}
            onNewVerification={handleNewVerification}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Header
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />

      <div className="app">
        <Sidebar />

        <div className="ekyb-layout ekyb-page">
          <EKYBStepNav
            currentStep={currentStep}
            completedSteps={completedSteps}
            micaRequired={micaRequired}
            onStepClick={goToStep}
          />

          <div className="ekyb-main-content">
            <div className="ekyb-main-content-inner">
              {renderStep()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EKYBVerification;
